import { createContext, createElement, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useMe } from "@/lib/auth";
import { ro } from "./strings.ro";
import { ru } from "./strings.ru";

export type Lang = "ru" | "ro";
export type TranslationKey = keyof typeof ru;

// Public: the keys of the dictionary, useful in callers that want type-safe
// constants (`const KEY: TranslationKey = "home.greeting"`).
export type { TranslationKey as I18nKey };

const DICTS: Record<Lang, Record<TranslationKey, string>> = { ru, ro };

const LOCAL_STORAGE_KEY = "rmnlng.locale";
const FALLBACK: Lang = "ru";

// ─── Initial detection ─────────────────────────────────
// Bootstrap order: localStorage → navigator.language → hardcoded RU.
// `useMe()` reconciliation happens later in the provider.
function detectInitialLang(): Lang {
  if (typeof window === "undefined") return FALLBACK;
  const fromStorage = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (fromStorage === "ru" || fromStorage === "ro") return fromStorage;
  const nav = window.navigator.language?.slice(0, 2).toLowerCase();
  if (nav === "ro") return "ro";
  if (nav === "ru") return "ru";
  return FALLBACK;
}

// ─── Interpolation ─────────────────────────────────────
function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const v = params[key];
    return v === undefined ? match : String(v);
  });
}

// ─── Context ───────────────────────────────────────────
type I18nValue = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  // Plural-aware lookup. Given a base key prefix + count, picks the
  // matching `${base}_${one|few|many|other}` variant via
  // `Intl.PluralRules`. Both target locales (ru / ro) use one/few/many.
  // The base itself doesn't need to exist in the dict — only the
  // variants. Type is relaxed to `string` because the prefix is a
  // virtual key.
  tPlural: (base: string, n: number, params?: Record<string, string | number>) => string;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  // Initial state is pulled synchronously from localStorage / navigator so
  // the first paint is already in the right language; the server-side `me`
  // query reconciles asynchronously (see effect below).
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());
  const { data: me } = useMe();

  // Reconcile with server-stored preference once it lands. If server says
  // something different from our local guess, trust the server (it's the
  // cross-device source of truth).
  useEffect(() => {
    if (!me?.locale) return;
    const serverLang = me.locale === "ro" ? "ro" : "ru";
    if (serverLang !== lang) {
      setLangState(serverLang);
      window.localStorage.setItem(LOCAL_STORAGE_KEY, serverLang);
    }
  }, [me?.locale, lang]);

  const setLang = (next: Lang) => {
    setLangState(next);
    window.localStorage.setItem(LOCAL_STORAGE_KEY, next);
    // Note: persisting to the server is the caller's job (Settings page uses
    // `useUpdateMe({ locale })`). Keeping the context synchronous means
    // the UI flips instantly regardless of network state.
  };

  const value = useMemo<I18nValue>(() => {
    const dict = DICTS[lang];
    const t = (key: TranslationKey, params?: Record<string, string | number>) => {
      const template = dict[key];
      if (template === undefined) {
        // Deliberate: show the key verbatim so the bug is visible on screen
        // (fallback-silently-to-English is worse — you never notice drift).
        if (import.meta.env.DEV) console.warn(`[i18n] Missing key: ${key}`);
        return key;
      }
      return interpolate(template, params);
    };
    const pluralRules = new Intl.PluralRules(BCP47[lang]);
    const tPlural = (base: string, n: number, params?: Record<string, string | number>) => {
      const cat = pluralRules.select(n);
      // Build the candidate list in priority order. Each is cast to
      // `TranslationKey` for the dict lookup; if it's not actually a
      // key, `dict[k]` returns undefined and we move on.
      const candidates = [`${base}_${cat}`, `${base}_other`, `${base}_many`, base] as TranslationKey[];
      const key = candidates.find((k) => dict[k] !== undefined);
      const template = key !== undefined ? dict[key] : undefined;
      if (template === undefined) {
        if (import.meta.env.DEV) console.warn(`[i18n] Missing plural key: ${base} (n=${n}, cat=${cat})`);
        return base;
      }
      return interpolate(template, { n, ...params });
    };
    return { lang, setLang, t, tPlural };
  }, [lang]);

  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

export function useT() {
  return useI18n().t;
}

export function useTPlural() {
  return useI18n().tPlural;
}

export function useLang(): Lang {
  return useI18n().lang;
}

// ─── Dates / numbers ──────────────────────────────────
// Thin wrappers over Intl that pass the active locale, so callers don't
// have to thread it manually every time. BCP-47 codes: "ru-RU", "ro-RO".
const BCP47: Record<Lang, string> = { ru: "ru-RU", ro: "ro-RO" };
export function localeTag(lang: Lang): string {
  return BCP47[lang];
}

export function formatDate(date: Date, lang: Lang, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(BCP47[lang], opts).format(date);
}
